import { useEffect, useRef, useState } from "react"

import { Form, useActionData, useNavigate, useOutletContext, useRouteLoaderData } from "react-router-dom"

import { Bee } from "~/src/components/BeeKit"
import { DateRangeFilter } from "~/src/components/Dashboard/DateRangeFilter"
import { StoreFilter } from "~/src/components/Dashboard/StoreFilter"
import {
  DashboardStorefrontInsightsOutletContext,
  DashboardStorefrontInsightsProps,
} from "~/src/components/DashboardStorefrontInsights/DashboardStorefrontInsightsShow"

const EXPORT_PANEL_OPTIONS = [
  { label: "Overview", value: "overview" },
  { label: "Order Volume", value: "order_volume" },
  { label: "Popular Products", value: "popular_products" },
  { label: "Shipping Destinations", value: "shipping_destinations" },
  { label: "Top Users", value: "top_users" },
  { label: "Recipient Feedback", value: "thank_you_notes" },
  { label: "Recipient Reactions", value: "recipient_reactions" },
]

export function DashboardStorefrontInsightsGenerateReportModal() {
  const {
    storeFilterSelections: initialStoreFilterSelections,
    startDate: initialStartDate,
    endDate: initialEndDate,
    allTime: initialAllTime,
    setFlashState,
  } = useOutletContext<DashboardStorefrontInsightsOutletContext>()
  const [storeFilterSelections, setStoreFilterSelections] = useState<string[]>(initialStoreFilterSelections)
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate)
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate)
  const [allTime, setAllTime] = useState<boolean>(initialAllTime)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const { stores, userEmail } = useRouteLoaderData(
    "dashboardStorefrontInsightsShowRoute"
  ) as DashboardStorefrontInsightsProps
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)
  const actionData = useActionData() as { flash?: Array<{ success?: string; error?: string }> } | undefined

  const handleCheckboxChange = () => {
    if (formRef.current) {
      const formData = new FormData(formRef.current)
      const selectedPanels = formData.getAll("reports[]")
      setIsSubmitDisabled(selectedPanels.length === 0)
    }
  }

  useEffect(() => {
    if (actionData) {
      setFlashState(actionData.flash?.[0] || null)
      if (actionData.flash?.[0]?.success) {
        navigate(-1)
      }
    }
  }, [actionData, navigate, setFlashState])

  return (
    <Bee.Modal onClose={() => navigate(-1)}>
      <Bee.ModalHeader className="flex items-center justify-between px-6 py-4">
        <h1 className="text-lg font-medium">Generate .csv report</h1>
        <Bee.ModalCloseButton />
      </Bee.ModalHeader>
      <div className="flex flex-col overflow-y-auto p-6">
        <Form ref={formRef} method="post" className="relative flex flex-col gap-4 pb-0 md:p-0">
          <div>
            <p className="mb-1 font-medium">Storefronts</p>
            <StoreFilter options={stores} selections={storeFilterSelections} onChange={setStoreFilterSelections} />
          </div>
          <div>
            <p className="mb-1 font-medium">Date Range</p>
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              allTime={allTime}
              onChange={(startDate, endDate, allTime) => {
                setStartDate(startDate)
                setEndDate(endDate)
                setAllTime(allTime)
              }}
            />
          </div>
          {storeFilterSelections.map((storeId) => (
            <input key={storeId} type="hidden" name="store_ids[]" value={storeId} />
          ))}
          <input type="hidden" name="start_date" value={startDate?.toISOString() || ""} />
          <input type="hidden" name="end_date" value={endDate?.toISOString() || ""} />
          <input type="hidden" name="all_time" value={allTime.toString()} />
          <input type="hidden" name="timezone" value={Intl.DateTimeFormat().resolvedOptions().timeZone} />
          <div>
            <p className="mb-1 font-medium">Data</p>
            {EXPORT_PANEL_OPTIONS.map(({ label, value }) => {
              return (
                <div className="mb-[4px]" key={value}>
                  <label>
                    <input
                      name="reports[]"
                      className="accent-navy-800 mr-2 rounded border-gray-300 hover:accent-blue-500"
                      type="checkbox"
                      value={value}
                      defaultChecked={true}
                      onChange={handleCheckboxChange}
                    />
                    {label}
                  </label>
                </div>
              )
            })}
          </div>
          <p className="mb-8 text-gray-500">This report will be emailed to you. ({userEmail})</p>
          <Bee.Button type="submit" className="drop-shadow-sm" disabled={isSubmitDisabled}>
            Generate Report
          </Bee.Button>
        </Form>
      </div>
    </Bee.Modal>
  )
}
